/* A server task (`TaskRow`) as the pages' `AgentTask`, and the pages' edits as
   the API's body. The pages edit a `ScheduleSpec`; the API holds a cron or a
   one-time `run_at`, both wall-clock in the task's zone — so a spec's "09:00"
   is the task's 9:00 and no time is ever converted here. A task whose zone is
   not the browser's says so in its chip. */
import { buildCron, chipLabel, initialSchedule } from "../TasksPage";

import type { AgentTask, ScheduleSpec, TaskPatch, TaskRow, TaskSchedule } from "@/shared/types";

// the three shapes `buildCron` writes; anything else is a custom cron
const DAILY = /^(\d{1,2}) (\d{1,2}) \* \* \*$/;
const WEEKLY = /^(\d{1,2}) (\d{1,2}) \* \* ([0-6](?:,[0-6])*)$/;
const MONTHLY = /^(\d{1,2}) (\d{1,2}) (\d{1,2}) \* \*$/;

const pad = (n: number) => String(n).padStart(2, "0");

export const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** "GMT+5:30", "EDT": short enough for a chip. */
export function zoneLabel(zone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value ?? zone;
}

/** The builder state that writes this schedule; a cron the builder can't write is "custom". */
export function toSpec(schedule: TaskSchedule): ScheduleSpec {
  if (schedule.kind === "once") {
    const [date, time] = schedule.run_at.split("T");
    return { ...initialSchedule, mode: "once", date, time };
  }
  const { cron } = schedule;
  const custom: ScheduleSpec = { ...initialSchedule, freq: "custom", cron };
  const [, minute, hour, third] = DAILY.exec(cron) ?? WEEKLY.exec(cron) ?? MONTHLY.exec(cron) ?? [];
  if (minute === undefined || Number(minute) > 59 || Number(hour) > 23) return custom;
  const time = `${pad(Number(hour))}:${pad(Number(minute))}`;
  if (DAILY.test(cron)) return { ...initialSchedule, freq: "daily", time };
  if (WEEKLY.test(cron)) return { ...initialSchedule, freq: "weekly", time, days: third.split(",").map(Number) };
  const day = Number(third);
  return day >= 1 && day <= 31 ? { ...initialSchedule, freq: "monthly", time, dayOfMonth: day } : custom;
}

export function toAgentTask(row: TaskRow): AgentTask {
  const spec = toSpec(row.schedule);
  const zone = row.schedule.timezone;
  return {
    id: row.id,
    name: row.name,
    prompt: row.prompt,
    spec,
    schedule: zone === browserZone() ? chipLabel(spec) : `${chipLabel(spec)} ${zoneLabel(zone)}`,
    cron: row.schedule.kind === "cron" ? row.schedule.cron : null,
    paused: row.paused,
    lastRun: row.last_run_at ?? undefined,
    createdAt: row.created_at,
    nextRunAt: row.next_run_at,
    timezone: zone,
    lastRunStatus: row.last_run?.status,
    lastRunThreadId: row.last_run?.thread_id,
    lastRunError: row.last_run?.error ?? null,
  };
}

export function toSchedule(spec: ScheduleSpec, timezone: string): TaskSchedule {
  if (spec.mode === "once") return { kind: "once", run_at: `${spec.date}T${spec.time}`, timezone };
  // buildCron answers null only for "custom", which carries its own cron
  return { kind: "cron", cron: spec.freq === "custom" ? spec.cron.trim() : buildCron(spec)!, timezone };
}

/** What the pages hand `onUpdateTask`, as the API's patch: only the fields it names.
    The chip, the cron and the history are derived, so they never go up. */
export function toTaskPatch(patch: Partial<AgentTask>, timezone: string): TaskPatch {
  return {
    ...(patch.name !== undefined && { name: patch.name }),
    ...(patch.prompt !== undefined && { prompt: patch.prompt }),
    ...(patch.paused !== undefined && { paused: patch.paused }),
    ...(patch.spec && { schedule: toSchedule(patch.spec, timezone) }),
  };
}
